import { apiClient } from '@/lib/api';
import type {
  AssignShiftInput,
  AssignShiftToTeamInput,
  CreateShiftInput,
  RemoveTeamShiftInput,
  ShiftAssignment,
  ShiftDetail,
  ShiftListItem,
  TeamAssignmentResult,
  TeamShiftAssignment,
  UpdateShiftInput,
} from '../types/shift';

const BASE = '/shift-management';

/** Raw snake_case row returned by GET /shift-management/team/:teamId/shifts. */
interface TeamShiftAssignmentRow {
  shift_id: number;
  title: string;
  code: string | null;
  color: string;
  start_time: string;
  end_time: string;
  start_date: string;
  end_date: string;
  member_count: number;
}

export const shiftsService = {
  async listShifts(params?: {
    limit?: number;
    skip?: number;
    sortColumn?: string;
    sortOrder?: string;
    search?: string;
  }): Promise<{ values: ShiftListItem[]; count: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.skip) query.set('skip', String(params.skip));
    if (params?.sortColumn) query.set('sortColumn', params.sortColumn);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    const response = await apiClient.get<any>(`${BASE}/list${qs ? `?${qs}` : ''}`);

    // Transform snake_case to camelCase
    return {
      count: response.count,
      values: response.values.map((shift: any) => ({
        id: shift.id,
        title: shift.title,
        code: shift.code,
        startTime: shift.start_time,
        endTime: shift.end_time,
        color: shift.color,
        assignedTeams: shift.assigned_teams,
        assignedUsers: shift.assigned_users,
        status: shift.status,
      })),
    };
  },

  async getShift(id: number): Promise<ShiftDetail> {
    const response = await apiClient.get<any>(`${BASE}/list?shiftId=${id}`);
    const shift = response.values;

    // Transform snake_case to camelCase
    return {
      id: shift.id,
      title: shift.title,
      code: shift.code,
      startTime: shift.start_time,
      endTime: shift.end_time,
      color: shift.color,
      assignedTeams: shift.assigned_teams,
      assignedUsers: shift.assigned_users,
      status: shift.status,
      assignments: (shift.assignments || []).map((a: any) => ({
        id: a.id,
        teamId: a.team_id,
        teamName: a.team_name,
        userId: a.user_id,
        userName: a.user_name,
        startDate: a.start_date,
        endDate: a.end_date,
      })),
    };
  },

  createShift(data: CreateShiftInput): Promise<ShiftDetail> {
    // Transform camelCase to snake_case for backend
    const payload = {
      title: data.title,
      code: data.code,
      start_time: data.startTime,
      end_time: data.endTime,
      color: data.color,
    };
    return apiClient.post<ShiftDetail>(`${BASE}/add-shift`, payload);
  },

  updateShift(id: number, data: UpdateShiftInput): Promise<ShiftDetail> {
    // Transform camelCase to snake_case for backend
    const payload: any = { id };
    if (data.title !== undefined) payload.title = data.title;
    if (data.code !== undefined) payload.code = data.code;
    if (data.startTime !== undefined) payload.start_time = data.startTime;
    if (data.endTime !== undefined) payload.end_time = data.endTime;
    if (data.color !== undefined) payload.color = data.color;

    return apiClient.post<ShiftDetail>(`${BASE}/update-shift`, payload);
  },

  deleteShift(id: number): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`${BASE}/delete-shift/${id}`);
  },

  assignShift(data: AssignShiftInput): Promise<ShiftAssignment> {
    // Transform camelCase to snake_case for backend
    const payload = {
      shift_id: data.shiftId,
      user_id: data.userId,
      start_date: data.startDate,
      end_date: data.endDate,
    };
    return apiClient.post<ShiftAssignment>(`${BASE}/assign-shift`, payload);
  },

  assignShiftToTeam(data: AssignShiftToTeamInput): Promise<TeamAssignmentResult> {
    // Transform camelCase to snake_case for backend
    const payload = {
      shift_id: data.shiftId,
      team_id: data.teamId,
      start_date: data.startDate,
      end_date: data.endDate,
    };
    return apiClient.post<TeamAssignmentResult>(`${BASE}/assign-shift-to-team`, payload);
  },

  deleteAssignment(id: number): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`${BASE}/delete-assign/${id}`);
  },

  async listTeamShifts(teamId: number): Promise<TeamShiftAssignment[]> {
    const response = await apiClient.get<{ values?: TeamShiftAssignmentRow[] }>(
      `${BASE}/team/${teamId}/shifts`,
    );

    // The endpoint returns raw snake_case rows; project them to the camelCase shape.
    return (response.values ?? []).map((a) => ({
      shiftId: a.shift_id,
      title: a.title,
      code: a.code ?? undefined,
      color: a.color,
      startTime: a.start_time,
      endTime: a.end_time,
      startDate: a.start_date,
      endDate: a.end_date,
      memberCount: a.member_count,
    }));
  },

  removeTeamShift(data: RemoveTeamShiftInput): Promise<{ status: boolean; values: number }> {
    // Transform camelCase to snake_case for backend
    const payload = {
      team_id: data.teamId,
      shift_id: data.shiftId,
      start_date: data.startDate,
      end_date: data.endDate,
    };
    return apiClient.delete<{ status: boolean; values: number }>(`${BASE}/delete-team-assign`, {
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
  },
};
